"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.ALL_INVOICE_VARIABLE_PATHS = exports.INVOICE_VARIABLE_NAMESPACE = exports.INVOICE_VARIABLE_GROUP_LABELS = exports.invoiceVariableGroupSchema = exports.INVOICE_VARIABLE_GROUPS = void 0;
const zod_1 = require("zod");
// Variable namespace for invoice templates. Derived from the backend's
// InvoiceRenderContext (invoice-preview-fixture.ts) — the exact shape
// InvoiceContextBuilder.build() returns at production render time — so the
// picker only offers variables that genuinely resolve.
// INVOICE_PREVIEW_FIXTURE must define a value for every path listed here
// (enforced by invoice-preview-fixture.spec.ts).
exports.INVOICE_VARIABLE_GROUPS = [
    'metycle',
    'invoice_details',
    'parties',
    'shipment',
    'goods',
    'amounts',
    'settlement',
    'computed_html',
    'adjustment_lines',
];
exports.invoiceVariableGroupSchema = zod_1.z.enum(exports.INVOICE_VARIABLE_GROUPS);
exports.INVOICE_VARIABLE_GROUP_LABELS = {
    metycle: 'Metycle Issuer Info',
    invoice_details: 'Invoice Details',
    parties: 'Parties',
    shipment: 'Shipment / BL',
    goods: 'Goods & Commercial Terms',
    amounts: 'Amounts',
    settlement: 'Settlement & Note Labels',
    computed_html: 'Computed Blocks (HTML)',
    adjustment_lines: 'Adjustment Lines (repeating)',
};
exports.INVOICE_VARIABLE_NAMESPACE = {
    metycle: {
        repeating: false,
        variables: [
            { path: 'metycle_name', label: 'Company Name' },
            { path: 'metycle_address', label: 'Address' },
            { path: 'metycle_city', label: 'City' },
            { path: 'metycle_country', label: 'Country' },
            { path: 'metycle_email', label: 'Email' },
            { path: 'metycle_phone', label: 'Phone' },
            { path: 'metycle_vat_id', label: 'VAT ID' },
            { path: 'metycle_iban', label: 'IBAN' },
            { path: 'metycle_bic', label: 'BIC' },
            { path: 'metycle_managing_directors', label: 'Managing Directors' },
            { path: 'metycle_register', label: 'Company Register' },
            { path: 'metycle_logo_base64', label: 'Logo (base64)' },
            { path: 'current_date', label: 'Current Date' },
        ],
    },
    invoice_details: {
        repeating: false,
        variables: [
            { path: 'invoice_number', label: 'Invoice Number' },
            { path: 'legal_number', label: 'Legal Number (issuer running number)' },
            { path: 'invoice_number_display', label: 'Invoice Number (legal once issued)' },
            { path: 'invoice_type_label', label: 'Invoice Type (display label)' },
            { path: 'invoice_direction', label: 'Invoice Direction' },
            { path: 'document_title', label: 'Document Title' },
            { path: 'invoice_date', label: 'Invoice / Document Date' },
            { path: 'service_date', label: 'Service Date' },
            { path: 'issue_date', label: 'Issue Date' },
            { path: 'original_due_date', label: 'Due Date (original)' },
            { path: 'order_number', label: 'Order / Contract Number' },
            { path: 'customer_number', label: 'Customer Number' },
            { path: 'payment_reference', label: 'Payment Reference' },
            { path: 'remarks', label: 'Remarks' },
            { path: 'contract_term_line', label: 'Contract Term' },
        ],
    },
    parties: {
        repeating: false,
        variables: [
            { path: 'counterparty_name', label: 'Counterparty Name' },
            { path: 'counterparty_vat_id', label: 'Counterparty VAT ID' },
            { path: 'issuer_company_name', label: 'Issuer Company Name' },
            { path: 'issuer_entity_legal_name', label: 'Issuer Entity Legal Name' },
            { path: 'issuer_entity_address', label: 'Issuer Entity Address' },
            { path: 'issuer_entity_country', label: 'Issuer Entity Country' },
            { path: 'issuer_entity_vat_id', label: 'Issuer Entity VAT ID' },
            { path: 'issuer_entity_registration', label: 'Issuer Entity Registration' },
            { path: 'issuer_contact_name', label: 'Issuer Contact Name' },
            { path: 'purchaser_name', label: 'Purchaser Name' },
            { path: 'purchaser_contact', label: 'Purchaser Contact' },
            { path: 'purchaser_address', label: 'Purchaser Address' },
            { path: 'consignee_name', label: 'Consignee Name' },
            { path: 'consignee_contact_name', label: 'Consignee Contact Name' },
            { path: 'consignee_address', label: 'Consignee Address' },
        ],
    },
    shipment: {
        repeating: false,
        variables: [
            { path: 'bl_order_name', label: 'BL Order Name' },
            { path: 'bl_number', label: 'BL Number' },
            { path: 'shipping_line', label: 'Shipping Line' },
            { path: 'port_of_loading', label: 'Port of Loading' },
            { path: 'port_of_discharge', label: 'Port of Discharge' },
            { path: 'final_destination', label: 'Final Destination' },
            { path: 'container_count', label: 'Container Count' },
            { path: 'container_numbers', label: 'Container Numbers' },
            { path: 'containers_text', label: 'Container Numbers (text)' },
            { path: 'packing', label: 'Packing' },
            { path: 'transport_mode', label: 'Transport Mode' },
            { path: 'vessel_voyage', label: 'Vessel / Voyage' },
            { path: 'eta', label: 'ETA' },
        ],
    },
    goods: {
        repeating: false,
        variables: [
            { path: 'description_of_goods', label: 'Description of Goods' },
            { path: 'isri_grade', label: 'ISRI Grade' },
            { path: 'loaded_quantity_mt', label: 'Loaded Quantity (MT)' },
            { path: 'contract_quantity_mt_formatted', label: 'Contract Quantity (MT)' },
            { path: 'shipping_description', label: 'Shipping Description' },
            { path: 'incoterms', label: 'Incoterms' },
            { path: 'incoterms_full', label: 'Incoterms (full line)' },
            { path: 'formatted_payment_terms', label: 'Payment Terms' },
            { path: 'quotation_period_line', label: 'Quotation Period Line' },
            { path: 'qp_start', label: 'Quotational Period Start' },
            { path: 'qp_end', label: 'Quotational Period End' },
            { path: 'payable_factor', label: 'Payable Factor' },
            { path: 'country_of_origin', label: 'Country of Origin' },
            { path: 'weight_tolerance_line', label: 'Weight Tolerance / Nominal' },
            { path: 'pricing_remarks_block', label: 'Pricing Remarks' },
        ],
    },
    amounts: {
        repeating: false,
        variables: [
            { path: 'currency', label: 'Currency' },
            { path: 'total_amount', label: 'Total Amount (raw)' },
            { path: 'vat_amount', label: 'VAT Amount (raw)' },
            { path: 'vat_rate', label: 'VAT Rate' },
            { path: 'unit_price_formatted', label: 'Unit Price (formatted)' },
            { path: 'invoice_subtotal_formatted', label: 'Invoice Subtotal (formatted)' },
            { path: 'total_net_weight_formatted', label: 'Total Net Weight (formatted)' },
            { path: 'total_amount_due_formatted', label: 'Total Amount Due (formatted)' },
            { path: 'net_amount_formatted', label: 'Net Amount (formatted)' },
            { path: 'vat_amount_formatted', label: 'VAT Amount (formatted)' },
            { path: 'total_amount_formatted', label: 'Total Amount (formatted)' },
            { path: 'downpayment_amount_raw', label: 'Downpayment Amount (raw)' },
            { path: 'total_with_vat_raw', label: 'Total with VAT (raw)' },
            { path: 'base_price_formatted', label: 'Base Price (formatted)' },
            { path: 'premium_discount_formatted', label: 'Premium / Discount (formatted)' },
            { path: 'worked_calc_line', label: 'Worked Calculation Line' },
        ],
    },
    settlement: {
        repeating: false,
        variables: [
            { path: 'market_reference_date', label: 'Market Reference Date' },
            { path: 'market_reference_price', label: 'Market Reference Price' },
            { path: 'applied_downpayment_amount', label: 'Applied Downpayment Amount' },
            { path: 'applied_provisional_amount', label: 'Applied Provisional Amount' },
            { path: 'adjusts_invoice_number', label: 'Adjusted Invoice Number' },
            { path: 'related_invoice_number', label: 'Related Invoice Number' },
            { path: 'note_title', label: 'Note Title' },
            { path: 'note_number_label', label: 'Note Number Label' },
            { path: 'note_number', label: 'Note Number' },
            { path: 'total_label', label: 'Total Label' },
            { path: 'note_reason', label: 'Note Reason' },
            { path: 'settlement_method', label: 'Settlement Method' },
            { path: 'supporting_details', label: 'Supporting Details' },
        ],
    },
    computed_html: {
        repeating: false,
        variables: [
            { path: 'pricing_basis_line', label: 'Pricing Basis Line' },
            { path: 'pricing_breakdown_html', label: 'Pricing Breakdown' },
            { path: 'payment_details_block', label: 'Payment Details Block' },
            { path: 'vat_note_html', label: 'VAT Note' },
            { path: 'section13_note', label: '§13 UStG Note' },
            { path: 'downpayment_applied_totals_row', label: 'Downpayment Applied Row' },
            { path: 'provisional_applied_totals_row', label: 'Provisional Applied Row' },
            { path: 'payment_instruction_line', label: 'Payment Instruction Line' },
        ],
    },
    adjustment_lines: {
        repeating: true,
        section: 'adjustment_lines',
        variables: [
            { path: 'ref', label: 'Ref.' },
            { path: 'title', label: 'Title' },
            { path: 'basis', label: 'Basis' },
            { path: 'details', label: 'Details' },
            { path: 'quantity', label: 'Quantity' },
            { path: 'uom', label: 'Unit of Measure' },
            { path: 'rate_formatted', label: 'Rate (formatted)' },
            { path: 'amount_formatted', label: 'Amount (formatted)' },
        ],
    },
};
exports.ALL_INVOICE_VARIABLE_PATHS = Object.values(exports.INVOICE_VARIABLE_NAMESPACE).flatMap((group) => group.variables.map((v) => v.path));
//# sourceMappingURL=invoice-variable-namespace.js.map